import { determineUGTLocale, getAvailableLanguages } from '@components/asset/modal/tabs/edit/helpers';
import { getLocalStorage, setLocalStorage } from '@helpers/local_storage';
import { UserViewOptions, UserViewOptionsKeys } from '@components/show_page/sections/view_options/ViewOptionTypes';
import { Locale } from '@components/common/language_menu/languagesMap';
import { SearchOperatorOptions } from '@components/show_page/sections/section_search/SearchTypes';

export enum SortOptions {
  UploadDateNewest = "created_at DESC",
  UploadDateOldest = "created_at ASC",
  UpdatedNewest = "updated_at DESC",
  UpdatedOldest = "updated_at ASC",
  NameAtoZ = "name ASC",
  NameZtoA = "name DESC",
  MostPopular = "score DESC",
  LeastPopular = "score ASC",
  CreationDateNewest = "date_time_original DESC",
  CreationDateOldest = "date_time_original ASC",
  Position = "position ASC",
  Relevancy = "relevancy DESC",
}

export const sortOptions = (searchSubmitted = false): { value: SortOptions; label: string }[] => {
  let options: { value: SortOptions; label: string }[] = [
    { value: SortOptions.UploadDateNewest, label: bfTranslate("Upload date (newest)") },
    { value: SortOptions.UploadDateOldest, label: bfTranslate("Upload date (oldest)") },
    { value: SortOptions.UpdatedNewest, label: bfTranslate("Updated (newest)") },
    { value: SortOptions.UpdatedOldest, label: bfTranslate("Updated (oldest)") },
    { value: SortOptions.NameAtoZ, label: bfTranslate("Name (A-Z)") },
    { value: SortOptions.NameZtoA, label: bfTranslate("Name (Z-A)") },
    { value: SortOptions.MostPopular, label: bfTranslate("Most popular") },
    { value: SortOptions.LeastPopular, label: bfTranslate("Least popular") },
  ];

  if (BFG.hasFeature("sort_by_original_create_date")) {
    options = [
      { value: SortOptions.CreationDateNewest, label: bfTranslate("Creation date (newest)") },
      { value: SortOptions.CreationDateOldest, label: bfTranslate("Creation date (oldest)") },
      ...options
    ];
  }

  if (BFG.brandfolderSettings?.allow_positioning) {
    options = [
      { value: SortOptions.Position, label: bfTranslate("Position") },
      ...options
    ];
  }

  if (searchSubmitted) {
    options = [
      { value: SortOptions.Relevancy, label: bfTranslate("Relevancy") },
      ...options
    ];
  }

  return options;
};

export const resolveDefaultSortOrder = (
  defaultSortOrder: SortOptions,
  searchSubmitted = false,
): SortOptions => {
  if (searchSubmitted) {
    return SortOptions.Relevancy;
  }

  if (defaultSortOrder.includes('position') && !BFG.brandfolderSettings?.allow_positioning) {
    return sortOptions()?.[0]?.value || SortOptions.UploadDateNewest;
  }

  return defaultSortOrder;
};

const fallbackLocale = (): Locale => {
  const BFG = window.BFG; // test workaround to use window BFG mock
  const { staticSiteLocale, ugtLocaleDefault } = BFG.locales;
  const staticSiteLocaleInWhitelisted = getAvailableLanguages().find((languageEntry) => (
    languageEntry.locale === staticSiteLocale
  ));
  return staticSiteLocaleInWhitelisted?.locale || ugtLocaleDefault;
};

export const getDefaultViewOptions = (defaultSortOrder: SortOptions, paginatePer: number, searchOperator?: SearchOperatorOptions): UserViewOptions => {
  const BFG = window.BFG; // test workaround to use window BFG mock
  const defaultSearchOperator = BFG.brandfolderSettings?.default_search_operator || SearchOperatorOptions.OR;
  const defaultStrictSearch = BFG.brandfolderSettings?.default_strict_search;

  return {
    assetsPerPage: paginatePer,
    // searchOperator will be passed in if we're on a search share link that has a search operator
    searchOperator: searchOperator || defaultStrictSearch ? SearchOperatorOptions.Strict : SearchOperatorOptions[defaultSearchOperator],
    selectedUGTLocale: fallbackLocale(),
    showAsGrid: true,
    showCustomFields: false,
    showSections: true,
    showEmptySections: true,
    sortOption: resolveDefaultSortOrder(defaultSortOrder),
    sortOptionSearch: resolveDefaultSortOrder(defaultSortOrder, true),
  };
};

export const setUserViewOptions = (defaultSortOrder: SortOptions, paginatePer: number, searchOperator?: SearchOperatorOptions): UserViewOptions => {
  const BFG = window.BFG; // test workaround to use window BFG mock
  const defaultValues = { ...getDefaultViewOptions(defaultSortOrder, paginatePer, searchOperator) };
  // resource specific view options for current user, don't load for share views
  if (BFG.showPageLite) return defaultValues;

  const userViewOptionsAll = JSON.parse(getLocalStorage(`userViewOptions_${BFG.currentUser.user_id}`));
  const userViewOptions = userViewOptionsAll?.[BFG.resource.key] || {};

  if (userViewOptions && Object.keys(userViewOptions).includes(UserViewOptionsKeys.SelectedUGTLocale)) {
    // i.e. ensure that UGT found in local storage is useable
    userViewOptions.selectedUGTLocale = determineUGTLocale();
  }

  const processedUserViewOptions = {};
  [...Object.values(UserViewOptionsKeys)].forEach((option) => {
    // if we're on a search share link that has a search operator, use that value instead of the local storage value
    if (searchOperator && option === UserViewOptionsKeys.SearchOperator) {
      processedUserViewOptions[option] = searchOperator;
    } else {
      processedUserViewOptions[option] = userViewOptions[option] ?? defaultValues[option];
    }
  });

  return processedUserViewOptions;
};

export const updateUserViewOptionsInLocalStorage = (userViewOptions: UserViewOptions): void => {
  // update local storage with new user view options for this resource after state has updated
  const userId = BFG.currentUser.user_id;
  const resourceKey = BFG.resource.key;
  const userViewOptionsAll = { ...JSON.parse(getLocalStorage(`userViewOptions_${userId}`)) };

  if (!userViewOptionsAll[resourceKey]) {
    userViewOptionsAll[resourceKey] = {};
  }

  if (!Object.keys(userViewOptions).length) {
    delete userViewOptionsAll[resourceKey]; // clean up record so local storage doesn't balloon
  } else {
    userViewOptionsAll[resourceKey] = userViewOptions;
  }

  setLocalStorage(`userViewOptions_${userId}`, JSON.stringify(userViewOptionsAll));
};
